import { useEffect, useState } from "react";
import { GasPump } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageSlot } from "@/components/ImageUpload";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const GAUGES = ["E", "1/4", "1/2", "3/4", "F"];
const EMPTY = { vehicle_id: "", fuel_type: "Diesel", odometer: "", gauge_before: "", gauge_after: "", cost: "" };

export default function FuelPanel() {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const vehicle = vehicles.find((v) => v.id === form.vehicle_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.gauge_before || !form.gauge_after || !form.odometer) {
      toast.error("Vehicle, odometer and both gauge readings are required");
      return;
    }
    if (!receipt) {
      toast.error("A fuel receipt photo is required before saving");
      return;
    }
    setBusy(true);
    try {
      await api.post("/fuel", { ...form, receipt });
      toast.success("Fuel log saved");
      setForm(EMPTY);
      setReceipt(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl space-y-5 rounded-sm border border-border bg-card p-6 sm:p-8" data-testid="fuel-form">
      <h3 className="flex items-center gap-2 font-display text-xl font-bold tracking-tight">
        <GasPump size={22} className="text-primary" /> Fuel Log
      </h3>
      <div className="space-y-2">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Vehicle *</Label>
        <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
          <SelectTrigger data-testid="fuel-vehicle" className="rounded-sm border-input bg-background focus:ring-primary">
            <SelectValue placeholder="Select vehicle" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate_number}</SelectItem>)}
          </SelectContent>
        </Select>
        {vehicle?.max_fuel_limit > 0 && (
          <p className="text-xs text-amber-400" data-testid="fuel-limit-hint">Max fuel limit for this vehicle: RM {vehicle.max_fuel_limit}</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Fuel Type *</Label>
          <Select value={form.fuel_type} onValueChange={(v) => setForm({ ...form, fuel_type: v })}>
            <SelectTrigger data-testid="fuel-type" className="rounded-sm border-input bg-background focus:ring-primary"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="Diesel">Diesel</SelectItem>
              <SelectItem value="Petrol">Petrol</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Odometer Reading *</Label>
          <Input data-testid="fuel-odometer" type="number" min="0" value={form.odometer} onChange={(e) => setForm({ ...form, odometer: e.target.value })}
            className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Gauge Before *</Label>
          <Select value={form.gauge_before} onValueChange={(v) => setForm({ ...form, gauge_before: v })}>
            <SelectTrigger data-testid="fuel-gauge-before" className="rounded-sm border-input bg-background font-mono focus:ring-primary"><SelectValue placeholder="E → F" /></SelectTrigger>
            <SelectContent className="bg-popover">
              {GAUGES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Gauge After *</Label>
          <Select value={form.gauge_after} onValueChange={(v) => setForm({ ...form, gauge_after: v })}>
            <SelectTrigger data-testid="fuel-gauge-after" className="rounded-sm border-input bg-background font-mono focus:ring-primary"><SelectValue placeholder="E → F" /></SelectTrigger>
            <SelectContent className="bg-popover">
              {GAUGES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Fuel Cost (RM)</Label>
          <Input data-testid="fuel-cost" type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })}
            className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Receipt Upload *</Label>
        <div className="max-w-xs">
          <ImageSlot label="Fuel Receipt Photo" value={receipt} onChange={setReceipt} testid="fuel-receipt-upload" />
        </div>
        <p className="text-[10px] text-muted-foreground">A photo of the fuel station receipt is mandatory — the log cannot be saved without it.</p>
      </div>
      <Button data-testid="fuel-submit" type="submit" disabled={busy}
        className="w-full rounded-sm bg-primary py-6 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
        {busy ? "Saving..." : "Save Fuel Log"}
      </Button>
    </form>
  );
}
